import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  Banner,
  BodyText,
  Can,
  ControlledDateOnlyPicker,
  ControlledTextField,
  useConfirmDialog,
  useFormDialog,
} from '@if/mui'
import { Stack } from '@mui/material'
import { access } from '@src/access'
import { useDeleteInteracWire, useMatchInteracWire, useResetInteracPossibleMatch } from '@src/api/interac-wire-api'
import { getToday } from '@src/services/DateTimeProvider'
import { InteracPossibleMatch, InteracWire } from '@src/types/interac'
import { Link2, MailWarning, Trash2 } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const buildMatchSchema = (receivedOn: DateOnly) =>
  yup.object({
    dateOverride: yup.dateOnly().nullable().default(null).min(receivedOn),
  })

type MatchSchemaValues = yup.InferSubmit<ReturnType<typeof buildMatchSchema>>

const discardSchema = yup.object({
  reason: yup.text().required().default(''),
})

interface MatchDialogProps {
  wire: InteracWire
  candidate: InteracPossibleMatch
}

export interface InteracTerminalActions {
  confirmMatch: (wire: InteracWire, candidate: InteracPossibleMatch) => Promise<boolean>
  confirmNoMatch: (wire: InteracWire) => Promise<boolean>
  confirmDiscard: (wire: InteracWire) => Promise<boolean>
}

export const useInteracTerminalActions = (wire: InteracWire | undefined): InteracTerminalActions => {
  const { t } = useTranslation()
  const { mutateAsync: matchInteracWire } = useMatchInteracWire()
  const { mutateAsync: resetPossibleMatch } = useResetInteracPossibleMatch()
  const { mutateAsync: discardInteracWire } = useDeleteInteracWire()

  const matchSchema = useMemo(() => buildMatchSchema(new DateOnly(wire?.receivedOn ?? getToday())), [wire?.receivedOn])

  const loanLabel = useCallback(
    (candidate: InteracPossibleMatch) =>
      `${t(`enum.customerType.${candidate.ppaCustomerTypeName}`)} · ${candidate.referenceNumber}`,
    [t],
  )

  const matchDialog = useFormDialog<MatchSchemaValues, MatchDialogProps, InteracWire>({
    icon: Link2,
    title: t('interacWire.confirmMatch.title'),
    subtitle: (ctx) => loanLabel(ctx.props.candidate),
    confirmLabel: t('interacWire.confirmMatch.confirm'),
    confirmIcon: Link2,
    maxWidth: 'sm',
    fullWidth: true,
    schema: matchSchema,
    content: ({ form, props }) => (
      <Stack spacing={2}>
        <Banner severity="warning">
          {t('interacWire.confirmMatch.body', {
            amount: formatCurrency(props.wire.amount),
            loan: loanLabel(props.candidate),
          })}
        </Banner>
        <Can {...access.overrideInteracTransferDate}>
          <ControlledDateOnlyPicker
            control={form.control}
            name="dateOverride"
            label={t('interacWire.dateOverride')}
            slotProps={{ textField: { helperText: t('interacWire.dateOverrideHelper') } }}
            clearable
            fullWidth
          />
        </Can>
      </Stack>
    ),
    onSubmit: (values, ctx) =>
      matchInteracWire({
        id: ctx.props.wire.id,
        versionTag: ctx.props.wire.versionTag,
        loanId: ctx.props.candidate.loanId,
        dateOverride: values.dateOverride?.toString() ?? null,
      }),
  })

  const noMatchDialog = useConfirmDialog<InteracWire>({
    icon: MailWarning,
    title: t('interacWire.confirmNoMatch.title'),
    subtitle: (ctx) => ctx.props.sentBy,
    confirmLabel: t('interacWire.confirmNoMatch.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    message: (ctx) => (
      <Banner severity="warning">{t('interacWire.confirmNoMatch.body', { email: ctx.props.senderEmail })}</Banner>
    ),
    onConfirm: async (ctx) => {
      await resetPossibleMatch(ctx.props.id)
    },
  })

  const discardDialog = useFormDialog<yup.InferSubmit<typeof discardSchema>, InteracWire, InteracWire>({
    icon: Trash2,
    title: t('interacWire.confirmDiscard.title'),
    subtitle: (ctx) => ctx.props.sentBy,
    confirmLabel: t('interacWire.confirmDiscard.confirm'),
    confirmIcon: Trash2,
    confirmTone: 'destructive',
    maxWidth: 'sm',
    fullWidth: true,
    schema: discardSchema,
    content: ({ form }) => (
      <Stack spacing={2}>
        <BodyText>{t('interacWire.confirmDiscard.body')}</BodyText>
        <ControlledTextField
          control={form.control}
          name="reason"
          label={t('interacWire.confirmDiscard.reason')}
          autoFocus
          fullWidth
          multiline
          minRows={2}
        />
      </Stack>
    ),
    onSubmit: (values, ctx) =>
      discardInteracWire({ id: ctx.props.id, versionTag: ctx.props.versionTag, reason: values.reason }),
  })

  return useMemo(
    () => ({
      confirmMatch: (target, candidate) =>
        matchDialog.create({ wire: target, candidate }).then((result) => result !== null),
      confirmNoMatch: (target) => noMatchDialog.open(target).then(Boolean),
      confirmDiscard: (target) => discardDialog.create(target).then((result) => result !== null),
    }),
    [discardDialog, matchDialog, noMatchDialog],
  )
}
